import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import PlaceResult = google.maps.places.PlaceResult;
import { AddressObject } from '@appyvet/vetbooker-definitions/dist/register_user';
import { AddressRequirements } from '@appyvet/vetbooker-definitions/dist/client_patient_details';
import { AddressConfigService } from './address-config.service';
import {
  PMSAddressConfig,
  AddressFieldConfig,
} from './address-config.interface';
import { PMS } from '../../enums/pms-name.enum';

// Extended AddressObject to support PMS-specific fields
interface ExtendedAddressObject extends AddressObject {
  nameNo?: string; // Merlin PMS
  hNameNo?: string; // Teleos PMS
  streetAddress?: string; // ezyVet, Provet PMS
  streetAddress2?: string; // ezyVet PMS
  secondLine?: string; // Provet PMS
  thirdLine?: string; // Provet PMS
  street?: string; // Teleos PMS
  locality?: string; // Teleos PMS
  suburb?: string; // ezyVet PMS
  city?: string; // ezyVet, Provet PMS
  zipCode?: string; // ezyVet, RxWorks PMS
  zipcode?: string; // RxWorks PMS
  postCode?: string; // Provet PMS
  address4?: string; // Teleos PMS - County field
  state?: string; // All PMS systems
}

export interface AddressFormItem {
  address: ExtendedAddressObject;
  form: UntypedFormGroup;
  firstLoad?: boolean;
}

export const ADDRESS_1 = 'Address Line 1';
export const ADDRESS_2 = 'Address Line 2';
export const TOWN = 'Town';
export const COUNTY = 'County/State';
export const POSTCODE = 'Postcode';

@Component({
  selector: 'app-address-lookup',
  templateUrl: './address-lookup.component.html',
  styleUrls: ['./address-lookup.component.scss'],
})
export class AddressLookupComponent implements OnInit, OnChanges {
  addressSearchForm: UntypedFormGroup;
  alphanumericRegexp = RegExp(
    '^[\\p{L}\\p{N} _-]*[\\p{L}\\p{N}-][\\p{L}\\p{N} _-]*$',
    'u',
  );
  addressForm: UntypedFormGroup;

  @Input() showAddressForm: boolean;
  @Input() countryCode: string;
  @Input() address: ExtendedAddressObject;
  @Input() isNotClientDetailsPage: boolean;
  @Input() clinicCode: string;
  @Input() initialPostcode: string;
  @Input() requirements: AddressRequirements;
  @Input() addressChangeText: string;
  @Input() pmsType: string = 'default'; // New input for PMS type

  // Dynamic configuration
  currentConfig: PMSAddressConfig;
  addressFields: AddressFieldConfig[];

  addressValidationMessages: {
    [key: string]: { type: string; message: string }[];
  } = {
    address1: [
      {
        type: 'required',
        message: 'You must enter the first line of your address',
      },
      {
        type: 'pattern',
        message:
          'You cannot include special characters, only numbers, letters and spaces',
      },
    ],
    town: [{ type: 'required', message: 'You must enter a town' }],
    county: [{ type: 'required', message: 'You must enter a county' }],
    address4: [
      { type: 'required', message: 'You must enter a county' },
      {
        type: 'pattern',
        message:
          'You cannot include special characters, only numbers, letters and spaces',
      },
    ],
    postcode: [
      { type: 'required', message: 'You must enter a postcode' },
      {
        type: 'pattern',
        message: `Oops, looks like we didn't recognize that as a postcode`,
      },
      { type: 'maxlength', message: 'Postcode must be less than 8 characters' },
      { type: 'minlength', message: 'Postcode must be at least 4 characters' },
    ],
  };

  @Output() addressCancel = new EventEmitter<boolean>();
  @Output() addressSave = new EventEmitter<AddressFormItem>();
  loadingAddress$ = new BehaviorSubject<boolean>(false);
  selectedAddress: ExtendedAddressObject;

  constructor(
    private addressConfigService: AddressConfigService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild form when requirements or pmsType changes
    if (changes['requirements'] || changes['pmsType']) {
      this.initializeComponent();
    }
  }

  private initializeComponent(): void {
    // Load configuration based on PMS type
    this.currentConfig = this.addressConfigService.getAddressConfig(
      this.pmsType,
    );
    this.addressFields = this.currentConfig.fields.sort(
      (a, b) => a.order - b.order,
    );
    this.addressValidationMessages = this.currentConfig.validationMessages;

    if (!this.addressChangeText) {
      this.addressChangeText =
        'Are you sure you need to change your address? Remember, altering your details here' +
        ' will update your customer record.';
    }

    this.addressSearchForm = new UntypedFormGroup({
      address1: new UntypedFormControl(''),
      postcode: new UntypedFormControl(this.initialPostcode || '', [
        Validators.required,
      ]),
    });

    this.buildDynamicForm();
    this.addressSave.emit({
      address: null,
      form: this.addressForm,
      firstLoad: true,
    });
  }

  private buildDynamicForm(): void {
    // Store current form values to preserve user input
    const currentValues: { [key: string]: any } = {};
    if (this.addressForm) {
      this.addressFields.forEach((field) => {
        const control = this.addressForm.get(field.key);
        if (control) {
          currentValues[field.key] = control.value;
        }
      });
    }

    const formControls: { [key: string]: UntypedFormControl } = {};

    this.addressFields.forEach((field) => {
      const validators = this.buildValidators(field);
      // Use existing value or get initial value
      const value =
        currentValues[field.key] !== undefined
          ? currentValues[field.key]
          : this.getInitialValue(field.key);
      const control = new UntypedFormControl(value, validators);
      formControls[field.key] = control;
    });

    this.addressForm = new UntypedFormGroup(formControls);

    // Mark form as clean after rebuild
    this.addressForm.markAsPristine();
    this.addressForm.markAsUntouched();

    // Force UI update
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  private updateFormValidators(): void {
    if (!this.addressForm) return;

    this.addressFields.forEach((field) => {
      const control = this.addressForm.get(field.key);
      if (control) {
        const validators = this.buildValidators(field);

        // Clear existing state completely
        control.clearValidators();
        control.setErrors(null);
        control.markAsUntouched();
        control.markAsPristine();

        // Set new validators
        control.setValidators(validators);

        // Force update without triggering events
        control.updateValueAndValidity({ emitEvent: false, onlySelf: true });
      }
    });

    // Reset the entire form status
    this.addressForm.markAsUntouched();
    this.addressForm.markAsPristine();
    this.addressForm.updateValueAndValidity({ emitEvent: false });

    // Force change detection to update the UI
    this.cdr.detectChanges();
  }

  private buildValidators(field: AddressFieldConfig): any[] {
    const validators = [];

    // Check backend requirements first - if backend explicitly says field is not required, respect that
    const isRequiredFromPMS = field.required;
    const isRequiredFromBackend = this.isFieldRequiredByBackend(field.key);
    const isExplicitlyOptionalInBackend = this.isFieldExplicitlyOptional(
      field.key,
    );
    // Logic:
    // 1. If backend says explicitly false (not required), make it optional regardless of PMS config
    // 2. If backend says true, make it required
    // 3. If backend doesn't specify, use PMS config
    let isRequired = false;
    if (isExplicitlyOptionalInBackend) {
      isRequired = false; // Backend override: not required
    } else if (isRequiredFromBackend) {
      isRequired = true; // Backend says required
    } else {
      isRequired = isRequiredFromPMS; // Fallback to PMS config
    }

    if (isRequired) {
      validators.push(Validators.required);
    }

    if (
      field.key.includes('postcode') ||
      field.key.includes('zipCode') ||
      field.key.includes('postalCode') ||
      field.key.includes('zipPostal')
    ) {
      validators.push(Validators.pattern(this.alphanumericRegexp));
      if (this.countryCode === 'gb') {
        validators.push(Validators.minLength(4), Validators.maxLength(8));
      }
    } else if (field.type === 'text') {
      validators.push(Validators.pattern(this.alphanumericRegexp));
    }

    return validators;
  }

  private isFieldExplicitlyOptional(fieldKey: string): boolean {
    if (!this.requirements) {
      return false;
    }

    // Check if backend explicitly says this field is NOT required (false)
    const backendFieldMapping: { [key: string]: string } = {
      address1: 'address1',
      address: 'address1',
      streetAddress: 'address1',
      address2: 'address2',
      streetAddress2: 'address2',
      secondLine: 'address2',
      street: 'address2',
      address3: 'address3',
      town: 'town',
      city: 'town',
      suburb: 'town',
      locality: 'town',
      address4: 'county',
      county: 'county',
      state: 'state',
      stateProvince: 'state',
      region: 'state',
      postcode: 'postcode',
      zipCode: 'postcode',
      postalCode: 'postcode',
      postCode: 'postcode',
      zipcode: 'postcode',
      nameNo: 'nameNo',
      hNameNo: 'hNameNo',
    };

    const backendField = backendFieldMapping[fieldKey];
    if (!backendField) {
      return false;
    }

    const backendValue = (this.requirements as any)[backendField];
    const isExplicitlyFalse = backendValue === false;

    return isExplicitlyFalse;
  }

  private isFieldRequiredByBackend(fieldKey: string): boolean {
    if (!this.requirements) {
      return false;
    }

    // Map PMS field keys to backend requirement properties
    const backendFieldMapping: { [key: string]: string } = {
      // Standard address fields
      address1: 'address1',
      address: 'address1',
      streetAddress: 'address1',

      address2: 'address2',
      streetAddress2: 'address2',
      secondLine: 'address2',
      street: 'address2',

      address3: 'address3',
      town: 'town',
      city: 'city',
      suburb: 'town',
      locality: 'town',

      address4: 'address4',
      county: 'county',
      state: 'state',
      stateProvince: 'state',
      region: 'state',

      postcode: 'postcode',
      zipCode: 'postcode',
      postalCode: 'postcode',
      postCode: 'postcode',
      zipcode: 'postcode',

      // PMS-specific fields
      nameNo: 'nameNo',
      hNameNo: 'hNameNo',
    };

    const backendField = backendFieldMapping[fieldKey];
    if (!backendField) {
      return false;
    }

    const backendValue = (this.requirements as any)[backendField];
    const isExplicitlyTrue = backendValue === true;

    return isExplicitlyTrue;
  }
  private getInitialValue(fieldKey: string): string {
    if (!this.address) return '';

    // For PMS-specific fields, try direct access first
    if (this.address[fieldKey]) {
      return this.address[fieldKey];
    }

    // Map common field keys to address object properties as fallback
    const fieldMapping: { [key: string]: string } = {
      // Keep PMS-specific fields separate
      nameNo: 'nameNo',
      hNameNo: 'hNameNo',

      // Standard mappings
      address: 'address1',
      address1: 'address1',
      addressLine1: 'address1',
      streetAddress: 'address1',
      streetNumber: 'address1',
      houseNumber: 'address1',
      street: 'address2',
      address2: 'address2',
      addressLine2: 'address2',
      secondLine: 'address2',
      apartment: 'address2',
      streetName: 'address2',
      streetAddress2: 'address2',
      locality: 'address2',
      address3: 'town', // Note: address3 maps to 'town' in standard AddressObject
      address4: 'address4', // Note: address4 maps to 'county' in standard AddressObject
      thirdLine: 'county',
      town: 'town',
      city: 'city',
      suburb: 'town',
      municipality: 'town',
      county: 'county',
      stateProvince: 'county',
      state: 'county',
      region: 'county',
      postcode: 'postcode',
      postCode: 'postcode',
      postalCode: 'postcode',
      zipCode: 'postcode',
      zipcode: 'postcode',
      zipPostal: 'postcode',
    };

    const addressProperty = fieldMapping[fieldKey];
    const value = addressProperty ? this.address[addressProperty] || '' : '';
    return value;
  }
  addressSelected(address: PlaceResult) {
    this.showAddressForm = true;
    this.mapGoogleAddressToForm(address);
  }

  private mapGoogleAddressToForm(address: PlaceResult): void {
    // First, clear all form fields
    this.addressFields.forEach((field) => {
      this.addressForm.get(field.key)?.setValue('');
    });

    // Extract all available address components
    const addressComponents = {
      street_number: '',
      route: '',
      locality: '',
      sublocality: '',
      subpremise: '',
      postal_town: '',
      administrative_area_level_1: '',
      administrative_area_level_2: '',
      country: '',
      postal_code: '',
    };

    address.address_components.forEach((component) => {
      component.types.forEach((type) => {
        if (addressComponents.hasOwnProperty(type)) {
          addressComponents[type] = component.long_name;
        }
        // Special handling for administrative_area_level_2 with political type
        if (
          type === 'administrative_area_level_2' &&
          component.types.includes('political')
        ) {
          addressComponents['administrative_area_level_2'] =
            component.long_name;
        }
      });
    });

    // Now populate ALL available form fields based on what's available
    this.addressFields.forEach((field) => {
      const value = this.getComponentValueForField(
        field.key,
        addressComponents,
      );
      if (value) {
        this.addressForm.get(field.key)?.setValue(value);
      }
    });
  }

  private getComponentValueForField(
    fieldKey: string,
    addressComponents: any,
  ): string {
    const mappingRules: { [key: string]: string[] } = {
      hNameNo: ['street_number'],
      nameNo: ['street_number'],
      address: ['street_number', 'route'],
      address1: ['route'],
      addressLine1: ['street_number'],
      streetAddress: ['street_number'],
      street: ['route'],
      streetNumber: ['street_number'],
      houseNumber: ['street_number'],
      address2: ['locality', 'sublocality'],
      addressLine2: ['route'],
      secondLine: ['route'],
      apartment: ['subpremise'],
      streetName: ['route'],
      streetAddress2: ['route'],
      locality: ['locality', 'sublocality'],
      address3: ['postal_town', 'locality'],
      address4: ['administrative_area_level_2'],
      thirdLine: ['administrative_area_level_2', 'sublocality'],
      town: ['postal_town', 'locality'],
      city: ['postal_town', 'locality'],
      suburb: ['locality', 'sublocality'],
      municipality: ['postal_town', 'locality'],
      county: ['administrative_area_level_2'],
      stateProvince: ['administrative_area_level_2'],
      state: ['administrative_area_level_1'],
      region: ['administrative_area_level_2'],
      postcode: ['postal_code'],
      postCode: ['postal_code'],
      postalCode: ['postal_code'],
      zipCode: ['postal_code'],
      zipcode: ['postal_code'],
      zipPostal: ['postal_code'],
    };

    // If address1 and no nameNo field in config, combine street_number + route
    if (fieldKey === 'address1') {
      const hasNameNoField = this.addressFields.some((f) => f.key === 'nameNo');
      if (
        !hasNameNoField &&
        addressComponents.street_number &&
        addressComponents.route
      ) {
        return `${addressComponents.street_number} ${addressComponents.route}`.trim();
      } else if (!hasNameNoField && addressComponents.street_number) {
        return addressComponents.street_number;
      }
      return addressComponents.route || '';
    }

    const rules = mappingRules[fieldKey] || [];

    // For combined fields like 'address', concatenate street_number and route
    if (
      fieldKey === 'address' &&
      addressComponents.street_number &&
      addressComponents.route
    ) {
      return `${addressComponents.street_number} ${addressComponents.route}`.trim();
    }

    // For all other fields, find the first matching component
    for (const rule of rules) {
      if (addressComponents[rule]) {
        return addressComponents[rule];
      }
    }

    return '';
  }

  saveAddress() {
    const updateAddress = {} as ExtendedAddressObject;

    // Map all form fields back to address object
    this.addressFields.forEach((field) => {
      const value = this.addressForm.get(field.key)?.value;

      if (value) {
        // Store PMS-specific fields directly
        (updateAddress as any)[field.key] = value;

        // Also map to standard AddressObject structure for backward compatibility
        this.mapFieldToAddressObject(updateAddress, field.key, value);
      }
    });

    this.addressSave.emit({ address: updateAddress, form: this.addressForm });
  }
  private mapFieldToAddressObject(
    addressObject: ExtendedAddressObject,
    fieldKey: string,
    value: any,
  ): void {
    // Don't map PMS-specific fields to standard AddressObject properties
    // They're already stored directly on the address object
    if (['nameNo', 'hNameNo'].includes(fieldKey)) {
      return;
    }

    const fieldMapping: { [key: string]: string } = {
      address: 'address1',
      address1: 'address1',
      addressLine1: 'address1',
      streetAddress: 'address1',
      street: 'address2',
      streetNumber: 'address1',
      houseNumber: 'address1',
      address2: 'address2',
      addressLine2: 'address2',
      secondLine: 'address2',
      apartment: 'address2',
      streetName: 'address2',
      streetAddress2: 'address2',
      locality: 'address2',
      address3: 'town', // address3 maps to town in standard AddressObject
      address4: 'county', // address4 maps to county in standard AddressObject
      thirdLine: 'county',
      town: 'town',
      city: 'town',
      suburb: 'town',
      municipality: 'town',
      county: 'county',
      stateProvince: 'county',
      state: 'county', // state maps to county in standard AddressObject
      region: 'county',
      postcode: 'postcode',
      postCode: 'postcode',
      postalCode: 'postcode',
      zipCode: 'postcode',
      zipcode: 'postcode',
      zipPostal: 'postcode',
    };

    const addressProperty = fieldMapping[fieldKey];
    if (addressProperty && value) {
      addressObject[addressProperty] = value;
    }
  }

  cancelUpdate() {
    this.addressCancel.emit(false);
  }

  // Public method for template to check if a field is required
  isFieldRequired(fieldKey: string): boolean {
    if (!this.addressForm) return false;

    const control = this.addressForm.get(fieldKey);
    if (!control || !control.validator) return false;

    // Check if the control has required validator
    const validator = control.validator({} as any);
    return validator && validator['required'];
  }

  // Public method for template to check if field has errors
  hasFieldError(fieldKey: string, errorType: string): boolean {
    if (!this.addressForm) return false;

    const control = this.addressForm.get(fieldKey);
    if (!control) return false;

    return control.hasError(errorType) && (control.dirty || control.touched);
  }

  getFieldValidationMessages(fieldKey: string): any[] {
    return this.addressValidationMessages[fieldKey] || [];
  }
}
